import { HttpErrorResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { ApiService } from '@services/api/api.service';
import { Observable, of } from 'rxjs';
import { catchError, map, shareReplay } from 'rxjs/operators';

export type UserActivityScope = 'all' | 'office' | 'team' | 'none';

export interface UserActivityAccess {
  can_view: boolean;
  scope: UserActivityScope;
  can_manage_settings: boolean;
  office_ids: string[];
  team_ids: string[];
  /** Named offices/teams behind the scope, e.g. `[{ _id, name: 'YPM' }]`. */
  offices?: { _id: string; name: string }[];
  teams?: { _id: string; name: string }[];
  /** Ready to print, e.g. "Users in YPM" — an office admin must not be told "all offices". */
  scope_label?: string;
  user_count?: number;
}

const NO_ACCESS: UserActivityAccess = { can_view: false, scope: 'none', can_manage_settings: false, office_ids: [], team_ids: [] };

/**
 * Who may open the User Activity screens. Master admins, office admins and team leaders all
 * qualify, and team leadership isn't a role, so the rules live on the backend (`GET
 * /user-activity/access`) — never re-derive them from the role here.
 */
@Injectable({ providedIn: 'root' })
export class UserActivityAccessService {
  private cached: { userId: string; access$: Observable<UserActivityAccess> } = null;

  constructor(private api: ApiService) {}

  /** One request per signed-in user. A 403 is remembered; any other failure is retried on the next read. */
  get access$(): Observable<UserActivityAccess> {
    const userId = (this.api.getUserId() ?? '').toString();
    if (this.cached?.userId !== userId) {
      this.cached = {
        userId,
        access$: this.api.get('/user-activity/access').pipe(
          map((response: any) => ({ ...NO_ACCESS, ...(response?.data ?? {}) })),
          catchError((error: HttpErrorResponse) => {
            if (error?.status !== 403) {
              this.cached = null;
            }
            return of(NO_ACCESS);
          }),
          shareReplay(1)
        ),
      };
    }
    return this.cached.access$;
  }
}
